import React, { useState, ReactNode, useRef, useEffect } from "react";

interface ReadMoreLinesProps {
  children: ReactNode;
  mobileMaxRows?: number;
  desktopMaxRows?: number;
}

const ReadMoreLines: React.FC<ReadMoreLinesProps> = ({
  children,
  mobileMaxRows = 3,
  desktopMaxRows = 5,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [maxRows, setMaxRows] = useState(mobileMaxRows);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  // Check if content needs truncation
  useEffect(() => {
    const checkTruncation = () => {
      const element = contentRef.current;
      if (!element) return;
      
      // First measure with no clamp
      element.style.webkitLineClamp = 'none';
      element.style.overflow = 'visible';
      const fullHeight = element.scrollHeight;

      // Then measure with clamp
      element.style.webkitLineClamp = maxRows.toString();
      const clampedHeight = element.scrollHeight;
      
      // If the heights are different, content is truncated
      setIsTruncated(fullHeight > clampedHeight);

      // Reset styles based on expanded state
      if (!isExpanded) {
        element.style.overflow = 'hidden';
        element.style.webkitLineClamp = maxRows.toString();
      } else {
        element.style.overflow = 'visible';
        element.style.webkitLineClamp = 'none';
      }
    };

    // Update max rows based on screen width
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setMaxRows(mobileMaxRows);
      } else {
        setMaxRows(desktopMaxRows);
      }
      checkTruncation();
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [maxRows, mobileMaxRows, desktopMaxRows, isExpanded]);

  return (
    <div>
      <div className="relative">
        <div
          ref={contentRef}
          style={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            ...(isTruncated && {
              WebkitLineClamp: isExpanded ? 'none' : maxRows
            })
          }}
        >
          {children}
        </div>
        
        {isTruncated && !isExpanded && (
          <span className="absolute bottom-0 right-0 bg-white pl-1">
          <span>...</span>
            <span
              onClick={toggleReadMore}
              className="text-sm text-blue-500 hover:text-blue-700 cursor-pointer ml-1 inline"
            >
              Read More
            </span>
          </span>
        )}
      </div>
      {isTruncated && isExpanded && (
        <div className="flex justify-end">
          <span
            onClick={toggleReadMore}
            className="text-sm text-blue-500 hover:text-blue-700 cursor-pointer"
          >
            Read Less
          </span>
        </div>
      )}
    </div>
  );
};

export default ReadMoreLines; 